import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsomorphicLayoutEffect, useOnClickOutside } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';

import ListBox from '@ingka/list-box';
import ListView, { ListViewItem } from '@ingka/list-view';
import actionsIcon from '@ingka/ssr-icon/paths/ellipses-horizontal';
import personIcon from '@ingka/ssr-icon/paths/person';
import dataChartIcon from '@ingka/ssr-icon/paths/data-chart';
import copyIcon from '@ingka/ssr-icon/paths/copy';
import trashcanIcon from '@ingka/ssr-icon/paths/trash-can';
import Button from '@ingka/button';
import { useModal, useToast } from '@pcmp/react-providers';

import {
  CodeV2 as Code,
  useDeleteV2QrCodesBySlugMutation,
  usePostV2QrCodesMutation,
} from '@/redux/apis/codes-api';
import DeleteQrPrompt from '@/components/modals/delete-qr-prompt';
import { ChangeOwnerSider } from '@/components/siders/change-owner';
import {
  serializePermissions,
  serializeUser,
} from '@/lib/schemas/create-code-form/serialization';
import { useCtaReportPage } from '@/redux/features/cta/hooks';
import {
  useGetV1ConfigQuery,
  useGetV1UsersMeQuery,
} from '@/redux/apis/frontend-api';
import { useLoading } from '@/components/helpers/loading/context';
import { useUser } from '../../create-code/context';
import { canUserEditCode } from './permissions';
import { normalizeForm } from '@/lib/schemas/create-code-form/normalization';

type CssPosition = { left: number; top: number };

const assignPosition = (element: HTMLElement, css: CssPosition) => {
  element.style.left = `${css.left}px`;
  element.style.top = `${css.top}px`;
};

const getPositionByElement: (
  pivot: HTMLElement,
  container: HTMLElement
) => CssPosition = (pivot, container) => {
  const bounds = pivot.getBoundingClientRect();
  const listWidth =
    (container.querySelector('.list-box') as HTMLElement).offsetWidth || 350;

  return {
    left: bounds.right - listWidth,
    top: bounds.bottom,
  };
};

const MenuOption: FC<{
  label: string;
  icon: typeof actionsIcon;
  action?: () => void;
  disabled?: boolean;
  testId?: string;
}> = ({ label, icon, action, disabled, testId }) => {
  return (
    <ListViewItem
      leadingIcon={icon}
      leadingIconPosition="center"
      title={label}
      inset
      control="navigational"
      controlIcon={null}
      onClick={action}
      disabled={disabled}
      data-testId={testId}
    />
  );
};

const DeleteCodeButton: FC<{
  codeSlug: string;
  codeOwner: string;
  disabled?: boolean;
}> = ({ codeSlug, codeOwner, disabled }) => {
  // hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const { openModal } = useModal();
  const [deleteQrCode] = useDeleteV2QrCodesBySlugMutation();
  const { user } = useUser();

  // event handlers
  const handleDeleteCodeButton = async () => {
    if (disabled) return;
    openModal({
      component: DeleteQrPrompt,
      props: {
        confirm: confirmDeleteQrCode,
        showNotificationDisclaimer: codeOwner !== user.username,
      },
    });
  };
  const confirmDeleteQrCode = async () => {
    const response = await deleteQrCode({
      slug: codeSlug as string,
    });
    const isError = response && 'error' in response;
    const status = isError ? 'error' : 'success';
    const message = t(`page.codes.detail.messages.deleteCode.${status}`);
    // @ts-ignore
    pushToast({ message });

    if (!isError) {
      navigate('/codes', { replace: true });
    }
  };

  return (
    <MenuOption
      label={t('page.codes.detail.deleteCode')}
      icon={trashcanIcon}
      action={handleDeleteCodeButton}
      testId={'delete-code-button'}
      disabled={disabled}
    />
  );
};

const ChangeOwnerButton: FC<{
  code: Code;
  previousOwner: string;
  disabled?: boolean;
}> = ({ code, previousOwner, disabled }) => {
  // hooks
  const { t } = useTranslation();
  const { openModal } = useModal();

  // event handlers
  const openChangeOwnerModal = () => {
    if (disabled) return;
    openModal({
      component: ChangeOwnerSider,
      props: { code, previousOwner },
    });
  };
  return (
    <MenuOption
      label={t('page.codes.detail.changeOwner')}
      icon={personIcon}
      action={openChangeOwnerModal}
      disabled={disabled}
    />
  );
};

const ResetScanNumberButton: FC<{ codeSlug: string }> = ({ codeSlug }) => {
  // hooks
  const { t } = useTranslation();

  // event handlers
  const handleResetScanNumber = () => {
    // TODO: implement reset scan number
  };

  return (
    <MenuOption
      label={t('page.codes.detail.resetScanNumber')}
      icon={dataChartIcon}
      action={handleResetScanNumber}
    />
  );
};

const DuplicateButton: FC<{ code: Code; disabled?: boolean }> = ({
  code,
  disabled,
}) => {
  // hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const triggerReportPage = useCtaReportPage();
  const [createQrCode] = usePostV2QrCodesMutation();
  const { pushToast } = useToast();
  const { data: userData } = useGetV1UsersMeQuery();
  const { setLoading } = useLoading();

  // event handlers
  const handleDuplicate = async () => {
    if (!userData || disabled) return;
    setLoading(true);
    const { slug, codeName, ...rest } = code;
    const request = {
      body: {
        ...rest,
        user: serializeUser(userData),
        codeName: `${codeName} (copy)`,
        permissions: serializePermissions(normalizeForm(code), userData),
      },
    };
    const response = await createQrCode(request);
    const isError = response && 'error' in response;
    const status = isError ? 'error' : 'success';
    const message = t(`page.createCode.messages.createCode.${status}`);
    // @ts-ignore
    pushToast({ message });

    if (!isError) {
      // Redirect to the newly created code
      navigate(`/codes/${response.data.slug}`);
      // CTA form: Report the page view
      triggerReportPage('createdCode');
    }
    setLoading(false);
  };

  return (
    <MenuOption
      label={t('page.codes.detail.duplicate')}
      icon={copyIcon}
      action={handleDuplicate}
      disabled={disabled}
    />
  );
};

// Components
const FloatingSelector: FC<{
  cssPosition: CssPosition;
  code: Code;
}> = ({ cssPosition, code }) => {
  const { user } = useUser();
  const canEditCode = canUserEditCode(code, user);

  const listRef = useRef<HTMLUListElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!cssPosition || !listRef.current?.parentElement?.parentElement) return;

    assignPosition(listRef.current.parentElement.parentElement, cssPosition);
  }, [cssPosition]);

  return (
    <ListBox
      data-testid="dropdown"
      open={true}
      className="z-10 mt-2"
      style={cssPosition}
    >
      <ListView
        id="dropdownContent"
        size="small"
        className="flex flex-col justify-center"
        ref={listRef}
      >
        <ChangeOwnerButton
          code={code}
          previousOwner={code.user.email}
          disabled={!canEditCode}
        />
        {/* <ResetScanNumberButton codeSlug={code.slug as string} />*/}
        <DuplicateButton code={code} />
        <DeleteCodeButton
          codeSlug={code.slug as string}
          codeOwner={code.user.email}
          disabled={!canEditCode}
        />
      </ListView>
    </ListBox>
  );
};

export const CodeActions: FC<{ code: Code }> = ({ code }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: configData } = useGetV1ConfigQuery();

  const forceViewerMode =
    configData?.maintenanceStatus?.enabled &&
    configData?.maintenanceStatus?.forceViewerMode;

  useOnClickOutside(containerRef, (evt) => {
    // @ts-expect-error no closest method
    evt.target && !evt.target?.closest('#noClose') && setIsOpen(false);
  });

  const buttonRef = useRef(null);
  const [cssPosition, setCssPosition] = useState<CssPosition>({
    left: 0,
    top: 0,
  });

  useIsomorphicLayoutEffect(() => {
    if (!isOpen || !containerRef.current || !buttonRef.current) return;

    const css = getPositionByElement(buttonRef.current, containerRef.current);
    setCssPosition(css);
  }, [isOpen]);

  return (
    <>
      <Button
        iconOnly
        size="small"
        ssrIcon={actionsIcon}
        type="secondary"
        onClick={() => setIsOpen(!isOpen)}
        ref={buttonRef}
        data-testid="actions-button"
        disabled={forceViewerMode}
        className="w-10 h-8 rounded-full"
      />

      <div ref={containerRef}>
        {isOpen && <FloatingSelector cssPosition={cssPosition} code={code} />}
      </div>
    </>
  );
};
